import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Fingerprint } from "lucide-react";
import { useBiometric } from "@/hooks/useBiometric";
import { notePasswordLogin } from "@/components/Biometric";

const BG = "https://images.unsplash.com/photo-1646153114001-495dfb56506d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJhbCUyMG9mZmljZSUyMGludGVyaW9yfGVufDB8fHx8MTc4NTgzNjMyN3ww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, user, logout, setSession } = useAuth();
  const bio = useBiometric();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const u = await login(email, password, remember);
      notePasswordLogin();
      toast.success("Welcome", { description: "Signed in" });
      nav(u?.role === "client" ? "/my-business" : "/");
    } catch (ex) {
      const msg = formatApiErrorDetail(ex.response?.data?.detail) || ex.message;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  const switchAccount = async () => {
    await logout();
    setEmail("");
    setPassword("");
  };

  return (
    <div className="min-h-[100dvh] grid lg:grid-cols-2 bg-zinc-900 lg:bg-white relative" data-testid="login-page">
      {/* Mobile backdrop */}
      <div className="lg:hidden absolute inset-0 overflow-hidden" aria-hidden="true" data-testid="login-mobile-backdrop">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 scale-105" />
        <div className="absolute inset-0 bg-gradient-to-b from-zinc-900/40 via-zinc-900/70 to-zinc-950" />
      </div>
      {/* Left: form */}
      <div className="relative z-10 flex flex-col justify-between gap-6 p-5 pt-7 sm:p-10 lg:p-16 lg:border-r lg:border-zinc-200" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center gap-3 shrink-0 text-white lg:text-zinc-900">
          <div className="w-10 h-10 bg-white text-zinc-900 lg:bg-zinc-900 lg:text-white grid place-items-center font-heading font-black text-lg">M</div>
          <div>
            <div className="font-heading font-bold tracking-tight leading-none">Mind Maker 369</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 lg:text-zinc-500 mt-1">Tax Ops Console</div>
          </div>
        </div>

        <div className="lg:hidden text-white px-1" data-testid="login-mobile-hero">
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 font-semibold">Welcome back</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-2 leading-tight">Run the office<br />with clarity.</h1>
          <p className="text-xs text-white/70 mt-2">Tasks, billing, khata and your client portal — in one place.</p>
        </div>

        <form onSubmit={submit} className="max-w-md w-full space-y-5 lg:space-y-6 self-center bg-white/95 backdrop-blur-md p-5 sm:p-7 shadow-2xl shadow-black/40 lg:bg-transparent lg:backdrop-blur-0 lg:p-0 lg:shadow-none" autoComplete="off" data-testid="login-form">
          <div className="hidden lg:block">
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Mind Maker 369 · Sign In</div>
            <h1 className="font-heading font-black text-4xl lg:text-5xl tracking-tight mt-3 text-zinc-900">
              Run the office<br />with clarity.
            </h1>
            <p className="text-sm text-zinc-600 mt-4 max-w-sm">
              Sign in with <b>your own</b> Mind Maker 369 email and password. Never share your login — each team member has their own.
            </p>
          </div>
          <div className="lg:hidden text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-semibold">Sign in to continue</div>

          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Email</Label>
              <Input id="email" name="mindmaker-user" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false} placeholder="you@yourfirm.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-600">Password</Label>
              <Input id="password" name="mindmaker-pass" data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" placeholder="••••••••" />
            </div>
            {err ? <div data-testid="login-error" className="text-xs text-rose-600 bg-rose-50 border border-rose-200 px-3 py-2">{err}</div> : null}
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="login-remember-checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                className="h-4 w-4 accent-zinc-900 border-zinc-400"
              />
              <span className="text-xs text-zinc-700">Keep me signed in on this device <span className="text-zinc-400">(30 days)</span></span>
            </label>
            <Button data-testid="login-submit-btn" type="submit" disabled={busy} className="w-full h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-semibold tracking-wide">
              {busy ? "Signing in…" : "Sign in →"}
            </Button>
            {bio.supported && bio.hint ? (
              <button type="button" data-testid="login-biometric-btn" disabled={bio.busy} onClick={async () => { const d = await bio.unlock(remember); if (d) { setSession(d.user, d.token); toast.success("Welcome back"); nav(d.user?.role === "client" ? "/my-business" : "/"); } }}
                className="w-full h-11 border border-zinc-900 text-zinc-900 font-semibold tracking-wide flex items-center justify-center gap-2 hover:bg-zinc-900 hover:text-white transition-colors disabled:opacity-60">
                <Fingerprint size={16} /> {bio.busy ? "Verifying…" : "Unlock with fingerprint / Face ID"}
              </button>
            ) : null}
            <div className="flex items-center justify-between pt-1">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900">
                Forgot password?
              </Link>
              <span className="text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500">
                <Link to="/register" className="text-zinc-900 underline">New client? Register</Link>
              </span>
            </div>
            {user ? (
              <button
                type="button"
                data-testid="login-switch-user"
                onClick={switchAccount}
                className="w-full text-[11px] tracking-[0.14em] uppercase font-semibold text-zinc-500 hover:text-zinc-900 transition-colors"
              >
                Signed in as {user.email} · sign out
              </button>
            ) : null}
          </div>
        </form>

        <div className="text-[11px] tracking-[0.14em] uppercase text-white/50 lg:text-zinc-400 flex flex-wrap gap-x-4 gap-y-1 [&>a:hover]:text-white lg:[&>a:hover]:text-zinc-900">
          <span>Monday – Saturday · 09:00 – 18:00 IST</span>
          <Link to="/legal/privacy" data-testid="login-privacy-link" className="hover:text-zinc-900">Privacy</Link>
          <Link to="/legal/terms" data-testid="login-terms-link" className="hover:text-zinc-900">Terms</Link>
          <Link to="/install" data-testid="login-install-link" className="hover:text-zinc-900 lg:hidden">Get the app</Link>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img src={BG} alt="Office" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/70 font-semibold">Ops Manager · Handbook</div>
          <div className="font-heading font-bold text-2xl mt-3 max-w-md leading-tight">
            “No client work should be delayed. Balance the workload. Prioritise statutory deadlines.”
          </div>
        </div>
      </div>
    </div>
  );
}
